/**
 * SEO Utilities for generating dynamic meta tags and social sharing content
 */

// Helper function to get optimized image URL with sizing
export const getOptimizedImageUrl = (imageUrl, width = 1200, height = 630) => {
  if (!imageUrl) {return null;}

  // If it's already a full URL, return as is
  if (imageUrl.startsWith('http')) {
    return imageUrl;
  }

  // Add sizing parameters for optimization
  const separator = imageUrl.includes('?') ? '&' : '?';
  return `${imageUrl}${separator}width=${width}&height=${height}`;
};

// Extract poster image from various content types
export const extractPosterImage = (content, contentType) => {
  let posterUrl = null;

  switch (contentType) {
    case 'movie':
      // Try movie poster, then channel poster, then thumbnail
      posterUrl =
        content.poster || content.channel?.poster || content.channel?.thumb || content.thumb;
      break;

    case 'series':
      // Try series poster, then channel poster, then thumbnail
      posterUrl =
        content.poster || content.channel?.poster || content.channel?.thumb || content.thumb;
      break;

    case 'channel':
      // Try channel poster, then thumbnail
      posterUrl = content.poster || content.thumb || content.channel_logo;
      break;

    case 'episode':
      // Try episode thumb, then series poster, then channel poster
      posterUrl =
        content.thumb ||
        content.series?.poster ||
        content.channel?.poster ||
        content.channel?.thumb;
      break;

    default:
      posterUrl = content.poster || content.thumb;
  }

  return getOptimizedImageUrl(posterUrl, 1200, 630);
};

// Generate SEO data for movies
export const generateMovieSEO = (movie, channelData = null) => {
  const title = `Watch ${movie.title || movie.name} - Free Streaming on Sports.TV`;
  const description =
    movie.description ||
    `Stream ${
      movie.title || movie.name
    } for free on Sports.TV. Enjoy classic movies and timeless entertainment without subscription fees.`;

  const posterImage =
    extractPosterImage(movie, 'movie') || extractPosterImage(channelData, 'channel');

  return {
    title,
    description,
    image: posterImage,
    type: 'video.movie',
    url: `/movie/${movie.slug || movie._id}`,
  };
};

// Generate SEO data for series
export const generateSeriesSEO = (series, channelData = null) => {
  const title = `Stream ${series.title || series.name} Episodes - Sports.TV`;
  const description =
    series.description ||
    `Watch all episodes of ${
      series.title || series.name
    } for free on Sports.TV. Classic TV series and timeless shows available now.`;

  const posterImage =
    extractPosterImage(series, 'series') || extractPosterImage(channelData, 'channel');

  return {
    title,
    description,
    image: posterImage,
    type: 'video.tv_show',
    url: `/series/${series.slug || series._id}`,
  };
};

// Generate SEO data for episodes
export const generateEpisodeSEO = (episode, series, channelData = null) => {
  const episodeTitle = episode.title || `Episode ${episode.episode_number || ''}`;
  const seriesTitle = series?.title || series?.name || 'Series';

  const title = `${episodeTitle} - ${seriesTitle} | Sports.TV`;
  const description =
    episode.description ||
    `Watch ${episodeTitle} from ${seriesTitle} for free on Sports.TV. Stream classic TV episodes without subscription fees.`;

  const posterImage =
    extractPosterImage(episode, 'episode') ||
    extractPosterImage(series, 'series') ||
    extractPosterImage(channelData, 'channel');

  return {
    title,
    description,
    image: posterImage,
    type: 'video.episode',
    url: `/watch/${channelData?.slug || channelData?._id}/${episode._id}`,
  };
};

// Generate SEO data for channels/live TV
export const generateChannelSEO = channel => {
  const title = `${channel.title || channel.name} - Live TV Streaming | Sports.TV`;
  const description =
    channel.description ||
    `Watch ${
      channel.title || channel.name
    } live on Sports.TV. Free streaming with classic content and timeless entertainment.`;

  const posterImage = extractPosterImage(channel, 'channel');

  return {
    title,
    description,
    image: posterImage,
    type: 'website',
    url: `/live-tv/${channel.slug || channel._id}`,
  };
};

// Generate SEO data for static pages
export const generatePageSEO = (pageType, customData = {}) => {
  const seoData = {
    homepage: {
      title: 'Sports.TV - Free Streaming Movies & Classic TV Shows',
      description:
        'Stream classic movies and timeless TV shows for free on Sports.TV. No subscription required. Enjoy curated entertainment from the golden age of cinema and television.',
      url: '/',
    },
    about: {
      title: 'About Sports.TV - Free Streaming Platform Built with AI',
      description:
        'Learn about Sports.TV, the free streaming platform built with cutting-edge AI technology. Discover our mission to bring classic entertainment to everyone.',
      url: '/about',
    },
    contact: {
      title: 'Contact Sports.TV - Support & Customer Service',
      description:
        'Get in touch with Sports.TV support team. Contact us for technical help, content requests, or general inquiries about our free streaming service.',
      url: '/contact',
    },
    privacy: {
      title: 'Privacy Policy - Sports.TV Data Protection & CCPA Compliance',
      description:
        'Read Sports.TV privacy policy. Learn how we protect your data, our CCPA compliance, and your privacy rights on our free streaming platform.',
      url: '/privacy-policy',
    },
    terms: {
      title: 'Terms of Use - Sports.TV Legal Terms & Conditions',
      description:
        'Review Sports.TV terms of use and legal conditions for using our free streaming service. Understand your rights and responsibilities.',
      url: '/terms-of-use',
    },
    'do-not-sell': {
      title: 'Do Not Sell My Data - CCPA Opt-Out | Sports.TV',
      description:
        'Opt out of personal data sales under CCPA. Submit your "Do Not Sell My Data" request to Sports.TV for enhanced privacy protection.',
      url: '/do-not-sell',
    },
    'live-tv': {
      title: 'Live TV Streaming - Free Channels | Sports.TV',
      description:
        'Watch live TV channels for free on Sports.TV. Stream classic content, movies, and shows with our curated live television experience.',
      url: '/live-tv',
    },
    'on-demand': {
      title: 'On Demand - Free Movies & Shows | Sports.TV',
      description:
        'Browse the Sports.TV on-demand catalog — free movies, series, and sports programming, no subscription required.',
      url: '/on-demand',
    },
  };

  const defaultData = seoData[pageType] || seoData.homepage;

  return {
    ...defaultData,
    ...customData,
    type: 'website',
    image: customData.image || null, // Will use default Sports.TV image
  };
};

// Generate structured data (JSON-LD) for search engines
export const generateStructuredData = (content, contentType) => {
  const baseData = {
    '@context': 'https://schema.org',
    publisher: {
      '@type': 'Organization',
      name: 'Sports.TV',
      url: 'https://sports.tv',
      logo: {
        '@type': 'ImageObject',
        url: 'https://sports.tv/assets/images/sportstv-logo.png',
      },
    },
  };

  switch (contentType) {
    case 'movie':
      return {
        ...baseData,
        '@type': 'Movie',
        name: content.title || content.name,
        description: content.description,
        image: extractPosterImage(content, 'movie'),
        url: `https://sports.tv/movie/${content.slug || content._id}`,
        datePublished: content.created_at,
        genre: content.genre || 'Classic',
        duration: content.duration ? `PT${content.duration}M` : undefined,
      };

    case 'series':
      return {
        ...baseData,
        '@type': 'TVSeries',
        name: content.title || content.name,
        description: content.description,
        image: extractPosterImage(content, 'series'),
        url: `https://sports.tv/series/${content.slug || content._id}`,
        datePublished: content.created_at,
        genre: content.genre || 'Classic',
        numberOfEpisodes: content.playlist?.length,
      };

    case 'episode':
      return {
        ...baseData,
        '@type': 'TVEpisode',
        name: content.title,
        description: content.description,
        image: extractPosterImage(content, 'episode'),
        episodeNumber: content.episode_number,
        seasonNumber: content.season_number,
        partOfSeries: {
          '@type': 'TVSeries',
          name: content.series?.title || content.series?.name,
        },
      };

    default:
      return {
        ...baseData,
        '@type': 'WebSite',
        name: 'Sports.TV',
        url: 'https://sports.tv',
        description: 'Free streaming platform for classic movies and TV shows',
      };
  }
};
